import React from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Building2, FileText, Layers, CheckSquare, RefreshCw, LogOut, ChevronRight } from 'lucide-react';
import BaseNav from '@shared/components/common/BaseNav';
import { Button } from '@shared/components/ui/button';
import { useAuth } from '@shared/contexts/AuthContext';
import { useOfflineSync } from '@shared/hooks/useOfflineSync';
import { useOrgsQuery } from '@shared/hooks/useOrgsQuery';
import { offlineDataManager } from '@shared/lib/offlineData';
import FloatingNav from '@/components/common/FloatingNav';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { lastSyncTime, pendingOperations } = useOfflineSync();
  const { data: memberships = [] } = useOrgsQuery();
  const stats = offlineDataManager.getStatus();

  const statCard = (icon: React.ReactNode, label: string, value: number) => (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {icon}
      </div>
      <div>
        <div className="text-xl font-semibold leading-none">{value}</div>
        <div className="mt-1 text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <BaseNav />
      <div className="mx-auto mt-14 w-full max-w-2xl flex-1 px-4 py-8 md:px-6">
        <h1 className="mb-6 text-xl font-semibold">Profile</h1>

        {/* Identity */}
        <div className="mb-6 flex items-center gap-4 rounded-xl border border-border bg-card p-5">
          {user?.avatar ? (
            <img src={user.avatar} alt={user.name} className="h-16 w-16 rounded-full object-cover" />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
              <User className="h-8 w-8" />
            </div>
          )}
          <div className="min-w-0">
            <div className="truncate text-lg font-semibold">{user?.name}</div>
            <div className="truncate text-sm text-muted-foreground">{user?.email || 'Email unavailable — sign out and back in'}</div>
            <div className="mt-1 text-xs text-muted-foreground">Signed in with Google</div>
          </div>
        </div>

        {/* Workspace stats (local, offline-first) */}
        <div className="mb-6 grid grid-cols-3 gap-3">
          {statCard(<FileText className="h-5 w-5" />, 'Notes', stats.notesCount)}
          {statCard(<Layers className="h-5 w-5" />, 'Collections', stats.collectionsCount)}
          {statCard(<CheckSquare className="h-5 w-5" />, 'Tasks', stats.tasksCount)}
        </div>

        {/* Organizations */}
        <button
          onClick={() => navigate('/org')}
          className="mb-3 flex w-full items-center gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/40"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Building2 className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">Organizations</div>
            <div className="truncate text-xs text-muted-foreground">
              {memberships.length === 0
                ? 'Not in any organization yet'
                : memberships.map(m => m.org?.name).filter(Boolean).join(', ')}
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>

        {/* Sync */}
        <button
          onClick={() => navigate('/sync')}
          className="mb-8 flex w-full items-center gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/40"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <RefreshCw className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">Sync</div>
            <div className="text-xs text-muted-foreground">
              {pendingOperations > 0
                ? `${pendingOperations} change(s) waiting to sync`
                : lastSyncTime
                  ? `Last synced ${lastSyncTime.toLocaleString()}`
                  : 'Never synced on this device'}
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>

        {/* Sign out */}
        <Button variant="outline" className="gap-2 text-destructive hover:text-destructive" onClick={logout}>
          <LogOut className="h-4 w-4" /> Sign out
        </Button>
        <p className="mt-2 text-xs text-muted-foreground">
          Signing out clears this device's local data and pending changes.
        </p>
      </div>
      <FloatingNav />
    </div>
  );
}
